/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein';
import {CHECKOUT_MARKETING_OPT_IN_KEY} from '@amblique/king-living/app/constants/account';
import {
  PAYMENT_AMOUNT_ATTR,
  PAYMENT_AMOUNT_TYPE,
  PAYMENT_AMOUNT_TYPE_ATTR,
  calculatePaymentAmount,
  isPaymentAmountSectionVisible,
} from '@amblique/king-living/app/pages/checkout/util/payment-amount-utils';
import {useCheckoutCartInventory} from '@amblique/king-living/app/hooks/use-checkout-cart-inventory';
import {useBasketPaymentMethods} from '@amblique/king-living/app/hooks/use-basket-payment-methods';
import {useCheckoutWarehouseInventorySync} from '@amblique/king-living/app/hooks/use-checkout-warehouse-inventory-sync';
import {useDepositPercentage} from '@amblique/king-living/app/hooks/use-deposit-percentage';
import {usePayPalFlowInvalidate} from '@kingliving/paypal-pwa/app/hooks/use-paypal-flow-invalidate';
import {PAYMENT_PROVIDER} from '@amblique/king-living/app/constants/checkout';
import {basketHasPendingPayPalFlow} from '@kingliving/paypal-pwa/app/utils/paypal-utils';
import {toAppPathForLink} from '@amblique/king-living/app/pages/checkout/util/checkout-url-utils';
import {enqueueBasketMutation} from '@amblique/king-living/app/utils/basket-mutation-queue';
import {stripPdpConfigParamsFromPath} from '@amblique/king-living/app/utils/pdp-config-url';

const CheckoutContext = React.createContext();

/** SessionStorage key for the "continue shopping" return path */
export const CHECKOUT_CONTINUE_SHOPPING_KEY = 'king_checkout_continue_shopping';

/**
 * Paths we do not store as the "continue shopping" target (checkout flow only).
 * Cart is stored so that PDP → View Cart → Checkout → Continue shopping returns to cart.
 */
const CHECKOUT_PATH_PREFIXES = ['/checkout'];

/** ATC upsell intermediary — skip so PDP path is kept for checkout Continue shopping (bare path, after site/locale strip). */
const ADD_TO_CART_UPSELL_PATH_PREFIXES = ['/cart/upsell'];

function isCheckoutPath(path) {
  if (!path || typeof path !== 'string') return true;
  const p = toAppPathForLink(path).split('?')[0];

  return CHECKOUT_PATH_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

function isAddToCartUpsellPath(path) {
  if (!path || typeof path !== 'string') return false;
  const p = toAppPathForLink(path).split('?')[0];

  return ADD_TO_CART_UPSELL_PATH_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

/**
 * Store the given path as the "continue shopping" URL.
 * Call on every route change (from _app) when the current page is not checkout.
 * Cart is included so the last page before checkout is correct (e.g. cart when user came from cart).
 * PDP config query (`coverVariantId` / `selectedOptions`) is stripped so Continue shopping
 * after ATC does not rehydrate the pre-cart configuration.
 *
 * @param {string} path - pathname + search (e.g. '/p/123', '/cart', '/en-AU/p/123')
 */
export function storeContinueShoppingPath(path) {
  if (typeof window === 'undefined') return;
  try {
    let p = toAppPathForLink((path || '').trim() || '/');

    if (isCheckoutPath(p) || isAddToCartUpsellPath(p)) return;
    p = stripPdpConfigParamsFromPath(p);
    sessionStorage.setItem(CHECKOUT_CONTINUE_SHOPPING_KEY, p);
  } catch {
    // ignore
  }
}

/**
 * Store the referrer as the "continue shopping" URL when entering checkout.
 * Fallback when storeContinueShoppingPath was not set (e.g. direct URL to checkout).
 * Only stores same-origin referrers that are not checkout/confirmation pages.
 */
export function storeContinueShoppingReferrer() {
  if (typeof window === 'undefined') return;
  try {
    const referrer = document.referrer || '';

    if (!referrer) return;
    const origin = window.location.origin;

    if (!referrer.startsWith(origin)) return;
    let path = toAppPathForLink(referrer.slice(origin.length) || '/');

    if (isCheckoutPath(path) || isAddToCartUpsellPath(path)) return;
    path = stripPdpConfigParamsFromPath(path);
    sessionStorage.setItem(CHECKOUT_CONTINUE_SHOPPING_KEY, path);
  } catch {
    // ignore
  }
}

/**
 * Get the stored "continue shopping" path, or a default (e.g. '/cart').
 * @param {string} defaultPath - Fallback path when none stored or invalid
 * @returns {string} Path to use for the continue shopping link
 */
export function getContinueShoppingPath(defaultPath = '/cart') {
  if (typeof window === 'undefined') return defaultPath;
  try {
    const stored = sessionStorage.getItem(CHECKOUT_CONTINUE_SHOPPING_KEY);

    if (!stored || typeof stored !== 'string') {
      return defaultPath;
    }
    let path = toAppPathForLink(stored.trim());

    if (isCheckoutPath(path) || isAddToCartUpsellPath(path)) {
      return defaultPath;
    }

    return stripPdpConfigParamsFromPath(path);
  } catch {
    return defaultPath;
  }
}

const BILLING_SAME_KEY = 'king_billing_same_as_shipping';
const PAYMENT_PROVIDER_KEY = 'king_checkout_payment_provider';

function readBillingSame(basketId) {
  if (typeof window === 'undefined' || !basketId) return null;

  try {
    const raw = sessionStorage.getItem(BILLING_SAME_KEY);

    if (!raw) return null;

    const data = JSON.parse(raw);

    return data?.basketId === basketId ? data.value : null;
  } catch {
    return null;
  }
}

function writeBillingSame(basketId, value) {
  if (typeof window === 'undefined' || !basketId) return;

  try {
    sessionStorage.setItem(BILLING_SAME_KEY, JSON.stringify({basketId, value}));
  } catch {
    // ignore
  }
}

function readPaymentProvider(basketId) {
  if (typeof window === 'undefined' || !basketId) return null;

  try {
    const raw = sessionStorage.getItem(PAYMENT_PROVIDER_KEY);

    if (!raw) return null;

    const data = JSON.parse(raw);
    const value = data?.basketId === basketId ? data.value : null;

    return Object.values(PAYMENT_PROVIDER).includes(value) ? value : null;
  } catch {
    return null;
  }
}

function writePaymentProvider(basketId, provider) {
  if (
    typeof window === 'undefined' ||
    !basketId ||
    !Object.values(PAYMENT_PROVIDER).includes(provider)
  ) {
    return;
  }

  try {
    sessionStorage.setItem(PAYMENT_PROVIDER_KEY, JSON.stringify({basketId, value: provider}));
  } catch {
    // ignore
  }
}

function readMarketingOptIn() {
  if (typeof window === 'undefined') return false;

  try {
    // localStorage survives new-tab redirects (e.g. passwordless magic link email)
    const raw = localStorage.getItem(CHECKOUT_MARKETING_OPT_IN_KEY);

    if (!raw) return false;

    const data = JSON.parse(raw);

    return Boolean(data?.value);
  } catch {
    return false;
  }
}

function writeMarketingOptIn(basketId, value) {
  if (typeof window === 'undefined' || !basketId) return;

  try {
    if (value) {
      localStorage.setItem(CHECKOUT_MARKETING_OPT_IN_KEY, JSON.stringify({basketId, value: true}));
    } else {
      localStorage.removeItem(CHECKOUT_MARKETING_OPT_IN_KEY);
    }
  } catch {
    // ignore
  }
}

const CHECKOUT_STEPS_LIST = [
  'CONTACT_INFO',
  'SHIPPING_ADDRESS',
  'SHIPPING_OPTIONS',
  'PAYMENT',
  'REVIEW_ORDER'
];

const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, name, idx) => ({...acc, [name]: idx}), {});

export const CheckoutProvider = ({children}) => {
  const {formatMessage} = useIntl();
  const {data: customer} = useCurrentCustomer();
  const {data: basket, refetch: refetchCurrentBasket} = useCurrentBasket();
  const {isInventorySyncing} = useCheckoutWarehouseInventorySync();
  const {
    hasOutOfStockItems,
    isInventoryLoading: isProductsInventoryLoading,
    validateInventoryBeforePlaceOrder,
    loadProductsForIds,
    productsByProductId,
    shopperInventoryIds,
  } = useCheckoutCartInventory(basket);
  const isInventoryLoading = isProductsInventoryLoading || isInventorySyncing;
  // KINDW-270 / TSD SP-012 — deposit rate is merchant-configurable via the
  // `depositPercentage` site preference (do NOT hardcode 25%). Drives the
  // pay-now math AND the radio copy ("Pay {percentage} deposit now"). When the
  // preference is missing/invalid the deposit option is disabled and the
  // shopper pays in full.
  const {
    depositPercentage,
    depositRate,
    isDepositPercentageValid,
    formattedDepositPercentage,
    balanceDueDays,
  } = useDepositPercentage();
  const einstein = useEinstein();
  // KL-1105 — payment-method visibility is driven by Business Manager's per-site
  // active/inactive status via SCAPI `getPaymentMethodsForBasket` (fetched async,
  // keyed on basketId). This is the single source of truth for whether Stripe and
  // PayPal appear — replacing the old `stripeEnabled` pref and always-on PayPal.
  const {
    isStripeMethodActive: isStripeMethodActiveLive,
    isStripeAvailable: isStripeAvailableLive,
    isPayPalAvailable,
    isPaymentMethodsLoading,
    isPaymentMethodsResolved,
  } = useBasketPaymentMethods(basket?.basketId);

  // KL-1105 / KINDW-270 — latch Stripe availability across the place-order gap.
  // `useBasketPaymentMethods` is keyed on `basketId`; when `createOrder` consumes
  // the basket the live answer flips false. That would unmount `<PaymentElement>`
  // in payment-form.jsx (and `<Elements>` in the Stripe provider) right before
  // `stripe.confirmPayment(...)`, which then throws "elements should have a
  // mounted Payment Element". Keep the last affirmative answer while the basket
  // is transiently absent; once a basket is present again, honour a fresh
  // inactive answer from SCAPI. (Ref mutation during render — same pattern as
  // the currency/amount hold-overs in checkout-stripe-provider.jsx.)
  const stripeAvailableStickyRef = useRef(false);
  const stripeMethodActiveStickyRef = useRef(false);

  if (isStripeAvailableLive) {
    stripeAvailableStickyRef.current = true;
  } else if (basket?.basketId) {
    stripeAvailableStickyRef.current = false;
  }

  if (isStripeMethodActiveLive) {
    stripeMethodActiveStickyRef.current = true;
  } else if (basket?.basketId) {
    stripeMethodActiveStickyRef.current = false;
  }

  const isStripeAvailable =
    isStripeAvailableLive || (stripeAvailableStickyRef.current && !basket?.basketId);
  const isStripeMethodActive =
    isStripeMethodActiveLive || (stripeMethodActiveStickyRef.current && !basket?.basketId);
  const [step, setStep] = useState();
  const [billingSameAsShipping, setBillingSameAsShippingState] = useState(true);
  const [marketingOptIn, setMarketingOptInState] = useState(false);
  // KINDW-195 / KL-1080 — Payment Amount selection. Starts `null` (SSR / before
  // the basket loads) and defaults to `'full'` once the basket is available
  // (see the restore effect below) so "Pay full amount" is pre-selected without
  // an explicit click. A persisted `c_paymentAmountType` (returning/refreshing
  // shopper) or an explicit deposit pick overrides the default. When every line
  // is deposit-ineligible (in-stock and/or excluded from deposit; KL-1044 folds
  // in-stock into ineligibility) the section is hidden and `'full'` still wins.
  const [paymentAmountType, setPaymentAmountTypeState] = useState(null);
  // KINDW-195 — PAYMENT-05 #3: inline payment failure message rendered
  // above the selected payment method when `createOrder` fails (auth /
  // capture). Lives here (rather than locally in `pages/checkout/index.jsx`)
  // so the <Payment> partial can render it next to the form while
  // `submitOrder` lives on the page.
  const [placeOrderError, setPlaceOrderErrorState] = useState(null);
  // KINDW-270 — set true while a place-order failure error is being shown so
  // the basket auto-clear effect below doesn't wipe it. A failed Stripe order
  // reopens the basket (restoring `productItems`), which would otherwise trip
  // the auto-clear and hide the "payment failed" banner the moment it appears.
  // Reset by an explicit user action (retry / swap method clears the error).
  const placeOrderErrorStickyRef = useRef(false);
  // PayPal return/cancel messages — not cleared when basket refetches after cancel/failure API.
  const [checkoutBannerMessage, setCheckoutBannerMessageState] = useState(null);
  const [selectedPaymentProvider, setSelectedPaymentProviderState] = useState(
    PAYMENT_PROVIDER.CREDIT_CARD
  );
  const [isPaymentProviderHydrated, setIsPaymentProviderHydrated] = useState(false);
  // KINDW-270 — completeness of the Stripe `<PaymentElement>` (driven by its
  // `onChange` → `event.complete`). The card lives in Stripe's iframe, so this
  // is the only signal we have for "is the entered card/APM valid & complete".
  // Used to gate the "Continue to review" CTA so an invalid/incomplete card
  // can't advance to the review step (Stripe would otherwise only reject it at
  // place-order). Defaults `false` so the CTA stays disabled until a valid
  // method is entered.
  const [isStripePaymentComplete, setStripePaymentComplete] = useState(false);
  // KINDW — the Stripe `payment_method_type` currently selected in the
  // `<PaymentElement>` (driven by its `onChange` → `event.value.type`, e.g.
  // 'card', 'afterpay_clearpay', 'zip', 'klarna'). Tracked so place-order can
  // validate the deposit deny-list at submit time (rather than hiding methods
  // at deposit-amount selection, which forced an `<Elements>` re-mount and wiped
  // the entered card on every toggle). `null` until the shopper interacts.
  const [selectedStripePaymentMethodType, setSelectedStripePaymentMethodTypeState] =
    useState(null);
  const billingSameRestoredRef = useRef(false);
  const paymentAmountRestoredRef = useRef(false);
  // KL-1028 — the payment-amount `c_paymentAmount` value we most recently
  // enqueued to persist. Every path that writes the amount (the radio handler,
  // the restore effect, the section-visibility auto-lock) records it here so the
  // "keep c_paymentAmount in sync" effect below can tell an in-flight write apart
  // from a genuinely stale value. Without this, toggling Pay full ⇄ Pay deposit
  // fires TWO PATCHes: `{c_paymentAmountType, c_paymentAmount}` from the handler
  // and a redundant `{c_paymentAmount}` from the sync effect, because the effect
  // re-runs on the type change before the handler's PATCH has updated the cached
  // basket (so its persisted-vs-computed diff hasn't caught up yet).
  const pendingPaymentAmountRef = useRef(null);
  // KL-1028 — Delivery step drives this while shipping methods are loading /
  // the default method is being applied. The payment-amount sync effect and the
  // Stripe deferred-mode amount both honour it so we don't PATCH / refetch
  // against a pre-settlement total (e.g. list shipping price) and then again
  // against the promo/applied total a moment later.
  const [shippingTotalsPending, setShippingTotalsPendingState] = useState(false);
  const setShippingTotalsPending = useCallback((pending) => {
    setShippingTotalsPendingState(Boolean(pending));
  }, []);
  const paymentProviderBasketRef = useRef(null);

  // KL-1105 — hydrate the provider selection only once the applicable payment
  // methods have resolved for this basket, so we never fall back based on a
  // stale/empty list while the async `getPaymentMethodsForBasket` query is still
  // in flight. Availability is BM-driven (see useBasketPaymentMethods).
  if (
    basket?.basketId &&
    isPaymentMethodsResolved &&
    paymentProviderBasketRef.current !== basket.basketId
  ) {
    paymentProviderBasketRef.current = basket.basketId;
    const stored = readPaymentProvider(basket.basketId);
    const stripeAvailable = isStripeAvailable;
    const paypalAvailable = isPayPalAvailable;
    const storedValid = stored && (
      (stored === PAYMENT_PROVIDER.CREDIT_CARD && stripeAvailable) ||
      (stored === PAYMENT_PROVIDER.PAYPAL && paypalAvailable)
    );

    if (storedValid) {
      setSelectedPaymentProviderState(stored);
    } else if (!stripeAvailable && paypalAvailable) {
      setSelectedPaymentProviderState(PAYMENT_PROVIDER.PAYPAL);
      writePaymentProvider(basket.basketId, PAYMENT_PROVIDER.PAYPAL);
    } else if (stored && !storedValid) {
      const fallback = stripeAvailable ?
        PAYMENT_PROVIDER.CREDIT_CARD :
        (paypalAvailable ? PAYMENT_PROVIDER.PAYPAL : PAYMENT_PROVIDER.CREDIT_CARD);

      setSelectedPaymentProviderState(fallback);
      writePaymentProvider(basket.basketId, fallback);
    }

    setIsPaymentProviderHydrated(true);
  }

  const updateBasketMutation = useShopperBasketsMutation('updateBasket');
  const {invalidatePayPalFlow} = usePayPalFlowInvalidate();

  // Restore billingSameAsShipping from sessionStorage once basket is available
  useEffect(() => {
    if (!basket?.basketId || billingSameRestoredRef.current) return;
    billingSameRestoredRef.current = true;

    const stored = readBillingSame(basket.basketId);

    if (stored !== null) {
      setBillingSameAsShippingState(stored);
    }
  }, [basket?.basketId]);

  // Restore marketingOptIn from localStorage (survives redirect / basket merge)
  useEffect(() => {
    if (!basket?.basketId) return;

    if (readMarketingOptIn()) {
      setMarketingOptInState(true);
    }
  }, [basket?.basketId]);

  useEffect(() => {
    if (!basket?.basketId || !marketingOptIn) return;

    writeMarketingOptIn(basket.basketId, true);
  }, [basket?.basketId, marketingOptIn]);

  const setBillingSameAsShipping = useCallback((value) => {
    setBillingSameAsShippingState(value);
    writeBillingSame(basket?.basketId, value);
  }, [basket?.basketId]);

  const setMarketingOptIn = useCallback((value) => {
    writeMarketingOptIn(basket?.basketId, value);
    setMarketingOptInState(value);
  }, [basket?.basketId]);

  const setSelectedPaymentProvider = useCallback((provider) => {
    if (!Object.values(PAYMENT_PROVIDER).includes(provider)) {
      return;
    }

    setSelectedPaymentProviderState(provider);
    writePaymentProvider(basket?.basketId, provider);
    placeOrderErrorStickyRef.current = false;
    setPlaceOrderErrorState(null);
    setCheckoutBannerMessageState(null);
  }, [basket?.basketId]);

  // KINDW — record the method the shopper picks in the Stripe PaymentElement.
  // Changing it clears any prior place-order error (e.g. the deposit-ineligible
  // banner) so swapping to an eligible method removes the message immediately.
  const setSelectedStripePaymentMethodType = useCallback((type) => {
    setSelectedStripePaymentMethodTypeState(type ?? null);
    placeOrderErrorStickyRef.current = false;
    setPlaceOrderErrorState(null);
  }, []);

  // KINDW-195 — persist the chosen payment-amount type AND the resulting
  // monetary amount to the basket so the choice survives refreshes /
  // round-trips and the OMS downstream can read both the intent and the
  // value to charge. Local state is updated synchronously so the radio
  // reflects the click without waiting on the SCAPI round-trip.
  //
  // We deliberately PATCH `c_paymentAmountType` and `c_paymentAmount`
  // together in one request — splitting them would let the basket briefly
  // carry a mismatched pair (e.g. `deposit` + the old full amount), which
  // any concurrent OMS poll would see as the persisted state.
  const setPaymentAmountType = useCallback((value) => {
    if (value !== PAYMENT_AMOUNT_TYPE.FULL && value !== PAYMENT_AMOUNT_TYPE.DEPOSIT) {
      return;
    }

    // TSD SP-012 — block selecting deposit when the configured rate is
    // missing/invalid; the UI also disables the radio + shows the
    // "deposit unavailable" notice in this state.
    if (value === PAYMENT_AMOUNT_TYPE.DEPOSIT && !isDepositPercentageValid) {
      return;
    }

    setPaymentAmountTypeState(value);

    // KINDW — switching Pay full ⇄ Pay deposit clears any prior place-order
    // error (e.g. the "this method isn't available for deposit payments"
    // banner) so changing the amount resolves the message immediately.
    placeOrderErrorStickyRef.current = false;
    setPlaceOrderErrorState(null);

    if (!basket?.basketId) return;

    const amount = calculatePaymentAmount(
      basket,
      productsByProductId,
      value,
      depositRate,
      shopperInventoryIds
    );
    const typeIsSame = basket?.[PAYMENT_AMOUNT_TYPE_ATTR] === value;
    const amountIsSame = Number.isFinite(basket?.[PAYMENT_AMOUNT_ATTR]) &&
      Math.abs(basket[PAYMENT_AMOUNT_ATTR] - amount) < 0.005;

    if (typeIsSame && amountIsSame) return;

    // KL-1028 — record the amount we're about to persist so the sync effect
    // below doesn't fire a duplicate `{c_paymentAmount}` PATCH before this one's
    // response updates the cached basket.
    pendingPaymentAmountRef.current = amount;

    // KL-1029 — serialize the deposit/payment-amount PATCH through the shared
    // basket mutation queue. This effect-driven PATCH also fires when the basket
    // changes (see effects below), so without serialization it can race an
    // in-flight item removal and write a stale pre-removal basket to the cache.
    enqueueBasketMutation(() => updateBasketMutation.mutateAsync({
      parameters: {basketId: basket.basketId},
      body: {
        [PAYMENT_AMOUNT_TYPE_ATTR]: value,
        [PAYMENT_AMOUNT_ATTR]: amount,
      },
    })).catch(() => {});
  }, [
    basket,
    productsByProductId,
    shopperInventoryIds,
    depositRate,
    isDepositPercentageValid,
    updateBasketMutation
  ]);

  // Restore `paymentAmountType` from `basket.c_paymentAmountType` once the
  // basket is available. Also auto-lock to `'full'` when every line item is
  // deposit-ineligible — i.e. in-stock (KL-1044) and/or excluded from deposit
  // (FSD PAYMENT-01 update: hide + default to full). A mixed basket (≥1
  // deposit-eligible item) keeps the section and does NOT auto-lock.
  //
  // We wait on `productsByProductId` for the auto-lock path because the gating
  // rule depends on per-product data (ATS for in-stock, `c_isExcludedFromDeposit`
  // for the flag). Restoring a previously chosen value doesn't need the lookup.
  useEffect(() => {
    if (!basket?.basketId) return;
    if (paymentAmountRestoredRef.current) return;

    const stored = basket[PAYMENT_AMOUNT_TYPE_ATTR];

    if (stored === PAYMENT_AMOUNT_TYPE.FULL || stored === PAYMENT_AMOUNT_TYPE.DEPOSIT) {
      paymentAmountRestoredRef.current = true;
      setPaymentAmountTypeState(stored);

      return;
    }

    // KL-1080 — no persisted choice: "Pay full amount" is the default. Reflect
    // it in the UI as soon as the basket is available so the Full radio is
    // pre-selected and the Place Order CTA is ready without an explicit click.
    // A mixed/deposit-eligible basket keeps the deposit option available to
    // switch to. (Previously the section stayed unselected until the shopper
    // picked, and full was only auto-stamped when the section was hidden.)
    setPaymentAmountTypeState(PAYMENT_AMOUNT_TYPE.FULL);

    // Persisting the type + amount pair needs per-product data (the amount math
    // resolves the eligible/ineligible split). Wait for it, then stamp once so
    // the basket carries `c_paymentAmountType=full` + `c_paymentAmount` for the
    // OMS and for a returning/refreshing shopper.
    if (!productsByProductId) return;
    paymentAmountRestoredRef.current = true;

    const amount = calculatePaymentAmount(basket, productsByProductId, PAYMENT_AMOUNT_TYPE.FULL);

    if (!Number.isFinite(amount) || amount <= 0) return;

    // KL-1028 — see setPaymentAmountType: mark the in-flight amount so the sync
    // effect doesn't double-PATCH it.
    pendingPaymentAmountRef.current = amount;

    // KL-1029 — serialize against other basket mutations (see setPaymentAmountType).
    enqueueBasketMutation(() => updateBasketMutation.mutateAsync({
      parameters: {basketId: basket.basketId},
      body: {
        [PAYMENT_AMOUNT_TYPE_ATTR]: PAYMENT_AMOUNT_TYPE.FULL,
        [PAYMENT_AMOUNT_ATTR]: amount,
      },
    })).catch(() => {});
  }, [basket?.basketId, productsByProductId, shopperInventoryIds]);

  // Track basket-content changes so we can keep the auto-lock and the
  // visibility honest as items enter/leave the basket mid-flow.
  //   - Section transitions to hidden (every item becomes deposit-ineligible —
  //     in-stock and/or excluded) → force to `'full'` and persist (type + amount).
  //   - Section transitions back to visible → leave the current selection
  //     alone (FSD says "hidden + defaulted to full"; we keep `full` so the
  //     shopper isn't quietly demoted to an unselected state and the CTA
  //     stays enabled).
  const lastSectionVisibleRef = useRef(null);

  useEffect(() => {
    if (!basket?.basketId) return;
    if (!paymentAmountRestoredRef.current) return;
    if (!productsByProductId) return;

    const visible = isPaymentAmountSectionVisible(basket, productsByProductId, shopperInventoryIds);
    const previous = lastSectionVisibleRef.current;

    lastSectionVisibleRef.current = visible;

    if (!visible && previous !== false && paymentAmountType !== PAYMENT_AMOUNT_TYPE.FULL) {
      setPaymentAmountTypeState(PAYMENT_AMOUNT_TYPE.FULL);

      if (basket[PAYMENT_AMOUNT_TYPE_ATTR] !== PAYMENT_AMOUNT_TYPE.FULL) {
        const amount = calculatePaymentAmount(basket, productsByProductId, PAYMENT_AMOUNT_TYPE.FULL);

        // KL-1028 — see setPaymentAmountType: mark the in-flight amount so the
        // sync effect doesn't double-PATCH it.
        pendingPaymentAmountRef.current = amount;

        // KL-1029 — serialize against other basket mutations (see setPaymentAmountType).
        enqueueBasketMutation(() => updateBasketMutation.mutateAsync({
          parameters: {basketId: basket.basketId},
          body: {
            [PAYMENT_AMOUNT_TYPE_ATTR]: PAYMENT_AMOUNT_TYPE.FULL,
            [PAYMENT_AMOUNT_ATTR]: amount,
          },
        })).catch(() => {});
      }
    }
  }, [
    basket?.basketId,
    basket?.productItems,
    productsByProductId,
    shopperInventoryIds,
    paymentAmountType
  ]);

  const isPaymentAmountVisible = isPaymentAmountSectionVisible(
    basket,
    productsByProductId,
    shopperInventoryIds
  );

  // KL-1028 — the live selected payment amount (major units), computed from the
  // SAME inputs we persist to `c_paymentAmount`. Exposed so the Stripe provider
  // can drive the deferred-mode `<Elements>` amount from the selection in the
  // same render the shopper toggles Pay full ⇄ Pay deposit — in lockstep with the
  // deposit deny-list — instead of waiting on the `c_paymentAmount` PATCH to
  // round-trip. Reading it from the basket lags a render behind the deny-list
  // (which comes from `paymentAmountType`), so `<Elements>` would otherwise
  // update twice (old amount + new deny-list, then the correct amount) and Stripe
  // would refetch its elements session twice per toggle. `null` until a real
  // selection + product data exist; the provider falls back to the basket then.
  const paymentAmount = useMemo(() => {
    if (!basket?.basketId || !productsByProductId || !paymentAmountType) return null;

    const amount = calculatePaymentAmount(
      basket,
      productsByProductId,
      paymentAmountType,
      depositRate,
      shopperInventoryIds
    );

    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }, [basket, productsByProductId, paymentAmountType, depositRate, shopperInventoryIds]);

  // Keep the persisted `c_paymentAmount` in sync with the live basket whenever
  // either input to the math changes (the chosen type, the line items, the
  // chosen shipping method, or any total recalculation). Without this, a
  // shopper who picked deposit early and then changed delivery / removed an
  // item would leave a stale dollar value on the basket — and at place-order
  // the OMS would charge the old number.
  //
  // We diff against the persisted value (with a sub-cent tolerance) so the
  // PATCH only fires when the recomputed amount actually changes. Diffing
  // also means the effect is a no-op for shoppers who never pick anything.
  //
  // KL-1028 — while Delivery is still loading / applying the default method
  // (`shippingTotalsPending`), skip the PATCH so we don't write a pre-settlement
  // total and then immediately rewrite it. Clearing the flag re-runs this effect
  // and persists the settled amount once.
  useEffect(() => {
    if (!basket?.basketId) return;
    if (!productsByProductId) return;
    if (!paymentAmountType) return;
    if (shippingTotalsPending) return;

    const amount = calculatePaymentAmount(
      basket,
      productsByProductId,
      paymentAmountType,
      depositRate,
      shopperInventoryIds
    );

    if (!Number.isFinite(amount)) return;

    const persisted = basket[PAYMENT_AMOUNT_ATTR];
    const persistedIsSame = Number.isFinite(persisted) && Math.abs(persisted - amount) < 0.005;

    // KL-1028 — skip when this exact amount is already being persisted by another
    // path (the radio handler / restore / auto-lock). On a Pay full ⇄ Pay deposit
    // toggle this effect re-runs on the type change before the handler's PATCH has
    // updated the cached basket, so `persistedIsSame` is still false; the pending
    // guard prevents the otherwise-duplicate `{c_paymentAmount}` PATCH. A later
    // amount change (line item / shipping) computes a different value, misses the
    // guard, and PATCHes as normal.
    const pendingIsSame = pendingPaymentAmountRef.current !== null &&
      Math.abs(pendingPaymentAmountRef.current - amount) < 0.005;

    if (persistedIsSame || pendingIsSame) return;

    pendingPaymentAmountRef.current = amount;

    // KL-1029 — serialize against other basket mutations (see setPaymentAmountType).
    enqueueBasketMutation(() => updateBasketMutation.mutateAsync({
      parameters: {basketId: basket.basketId},
      body: {[PAYMENT_AMOUNT_ATTR]: amount},
    })).catch(() => {});
  }, [
    basket?.basketId,
    basket?.productItems,
    basket?.orderTotal,
    basket?.productSubTotal,
    basket?.shippingTotal,
    basket?.[PAYMENT_AMOUNT_ATTR],
    productsByProductId,
    shopperInventoryIds,
    depositRate,
    paymentAmountType,
    shippingTotalsPending
  ]);

  // KINDW-195 — PAYMENT-05 #3: stable callback so consumers (page-level
  // `submitOrder` + the <Payment> partial's "clear on retry") can call
  // through without re-rendering the provider every interaction. Defined ahead
  // of `flushPaymentAmountBeforePlaceOrder` because that callback lists it as a
  // dependency (the multi-tab guard surfaces its warning through this setter).
  const setPlaceOrderError = useCallback((message) => {
    // Sticky while a real error is showing; cleared when the message is reset
    // (retry / swap method both call this with null first).
    placeOrderErrorStickyRef.current = Boolean(message);
    setPlaceOrderErrorState(message ?? null);
    // KINDW-276 — a Stripe place-order outcome supersedes any lingering PayPal
    // return banner (they share the one inline error slot). This runs only on
    // deliberate place-order attempts/results, not the automatic Stripe
    // <PaymentElement> onChange (which uses the raw state setters), so it won't
    // wipe a freshly-shown PayPal error.
    setCheckoutBannerMessageState(null);
  }, []);

  // KINDW-270 — the payment-amount PATCHes above are fire-and-forget
  // (`.mutate()`) so the radio reflects the click instantly. Before
  // place-order we flush the current selection synchronously (awaited) so the
  // basket's `c_paymentAmount` / `c_paymentAmountType` are guaranteed current
  // when the Stripe PaymentIntent is created and when `createOrder` consumes
  // the basket.
  //
  // KINDW — multi-tab guard: a shopper can keep checkout open in one tab while
  // adding/removing items in another. The local `useCurrentBasket` cache (and
  // therefore the persisted `c_paymentAmount`, especially for the deposit case)
  // then goes stale, and place-order would otherwise charge the old figure. So
  // we ALWAYS refetch the live basket here and reconcile:
  //
  //   1. Resolve the correct amount TYPE for the fresh basket. If the Payment
  //      Amount section is no longer offered (e.g. an in-stock, deposit-
  //      ineligible item was added in another tab) deposit is invalid → the
  //      basket must be paid in full. Otherwise the shopper's selection stands.
  //   2. Compare the amount the shopper's (stale) tab believed it would charge
  //      against the amount for the fresh basket. If the type was demoted or
  //      the payable amount moved, the basket changed under the shopper — we
  //      DON'T place the order. Instead we sync the local selection + persist
  //      the reconciled pair, surface a warning, and return `blocked: true` so
  //      the shopper reviews the updated total/selection before retrying.
  //   3. Unchanged → persist the (possibly still-pending) pair and proceed.
  //
  // Returns `{basket, blocked}` — callers must abort place-order when `blocked`.
  const flushPaymentAmountBeforePlaceOrder = useCallback(async () => {
    if (!basket?.basketId) return {basket, blocked: false};

    const selectedType =
      paymentAmountType || basket?.[PAYMENT_AMOUNT_TYPE_ATTR] || PAYMENT_AMOUNT_TYPE.FULL;

    // Pull the authoritative basket from SCAPI so cross-tab edits (added /
    // removed items, changed totals) are reflected before we compute.
    let freshBasket = basket;

    try {
      const {data: basketsData} = await refetchCurrentBasket();
      const refetched =
        basketsData?.baskets?.find((b) => b?.basketId === basket.basketId) ??
        basketsData?.baskets?.[0];

      if (refetched) freshBasket = refetched;
    } catch {
      // Network/refetch failure — fall back to the cached basket so place-order
      // still proceeds with the best data we have rather than hard-blocking.
      freshBasket = basket;
    }

    // Deposit eligibility depends on per-product data (in-stock ATS +
    // `c_isExcludedFromDeposit`). After a cross-tab edit, the fresh basket may
    // contain items whose product data isn't in `productsByProductId` yet
    // (that query is still keyed to the pre-refetch line-up). Load any missing
    // items on demand so `isPaymentAmountSectionVisible` / `calculatePaymentAmount`
    // decide against COMPLETE data on the first attempt — otherwise the guard
    // blocks twice: once for the content change, then again on the next submit
    // once the added item's data loads and reveals deposit is no longer valid.
    const freshProductIds = (freshBasket?.productItems ?? []).map((i) => i.productId);
    const productsForDecision =
      (await loadProductsForIds(freshProductIds)) || productsByProductId;

    // Deposit is only offered while the Payment Amount section is visible for
    // the fresh basket; otherwise force full (in-stock item present, or every
    // line excluded from deposit).
    const sectionVisible = isPaymentAmountSectionVisible(
      freshBasket,
      productsForDecision,
      shopperInventoryIds
    );
    const reconciledType = sectionVisible ? selectedType : PAYMENT_AMOUNT_TYPE.FULL;

    const newAmount = calculatePaymentAmount(
      freshBasket,
      productsForDecision,
      reconciledType,
      depositRate,
      shopperInventoryIds
    );

    // Cross-tab change detector. We compare the *content* of the local (closure)
    // basket against the freshly refetched one using the same product-item
    // signature the step-revert effect uses. This is what makes the guard
    // robust: comparing the recomputed dollar amounts directly is racy — the
    // refetch (and the fire-and-forget `c_paymentAmount` sync PATCH fired when
    // the shopper re-picks a shipping method) can nudge totals between the
    // closure snapshot and the fresh read, producing a *false* "basket changed"
    // block on an otherwise-unchanged basket (the "have to click Submit twice"
    // bug). Line items / quantities only differ when another tab actually
    // added, removed, or re-quantified something — and any real amount change
    // is downstream of exactly that.
    const cartSignature = (b) => (b?.productItems ?? [])
      .map((i) => `${i.productId ?? ''}:${i.quantity ?? 1}`)
      .sort()
      .join('|');

    const typeChanged = reconciledType !== selectedType;
    const contentsChanged = cartSignature(basket) !== cartSignature(freshBasket);

    if (typeChanged || contentsChanged) {
      // Reconcile the local selection so the radio + order summary reflect the
      // new basket state: demote deposit → full when the section is now hidden,
      // otherwise keep the shopper's still-applicable selection.
      setPaymentAmountTypeState(reconciledType);

      // Persist the reconciled pair so the shopper's next place-order proceeds
      // against the correct amount. Non-fatal on failure — the block already
      // prevents placement and the sync effects re-persist once the fresh
      // basket + product inventory settle.
      if (Number.isFinite(newAmount) && newAmount > 0) {
        try {
          await enqueueBasketMutation(() => updateBasketMutation.mutateAsync({
            parameters: {basketId: freshBasket.basketId},
            body: {
              [PAYMENT_AMOUNT_TYPE_ATTR]: reconciledType,
              [PAYMENT_AMOUNT_ATTR]: newAmount,
            },
          }));
        } catch {
          // ignore — see note above
        }
      }

      setPlaceOrderError(
        formatMessage({
          defaultMessage:
            'Your basket changed, so the amount payable has been updated. Please review your order total and payment amount selection, then place your order again.',
          id: 'checkout.error.basket_changed_before_place_order',
        })
      );

      return {basket: freshBasket, blocked: true};
    }

    // Unchanged: make sure the basket carries the correct type + amount, then
    // proceed with place-order.
    if (!Number.isFinite(newAmount) || newAmount <= 0) {
      return {basket: freshBasket, blocked: false};
    }

    const typeIsSame = freshBasket?.[PAYMENT_AMOUNT_TYPE_ATTR] === reconciledType;
    const amountIsSame = Number.isFinite(freshBasket?.[PAYMENT_AMOUNT_ATTR]) &&
      Math.abs(freshBasket[PAYMENT_AMOUNT_ATTR] - newAmount) < 0.005;

    if (typeIsSame && amountIsSame) return {basket: freshBasket, blocked: false};

    const updated = await enqueueBasketMutation(() => updateBasketMutation.mutateAsync({
      parameters: {basketId: freshBasket.basketId},
      body: {
        [PAYMENT_AMOUNT_TYPE_ATTR]: reconciledType,
        [PAYMENT_AMOUNT_ATTR]: newAmount,
      },
    }));

    // Prefer the SCAPI response (carries the recalculated totals + the just-
    // written attributes); fall back to a local merge if the mutation helper
    // didn't echo the basket back.
    return {
      basket: updated ?? {
        ...freshBasket,
        [PAYMENT_AMOUNT_TYPE_ATTR]: reconciledType,
        [PAYMENT_AMOUNT_ATTR]: newAmount,
      },
      blocked: false,
    };
  }, [
    basket,
    productsByProductId,
    loadProductsForIds,
    depositRate,
    paymentAmountType,
    shopperInventoryIds,
    refetchCurrentBasket,
    updateBasketMutation,
    setPlaceOrderError,
    formatMessage,
  ]);

  const setCheckoutBannerMessage = useCallback((message) => {
    setCheckoutBannerMessageState(message ?? null);
  }, []);

  // Auto-clear the inline payment error when the basket changes in a way
  // that may have resolved the underlying problem (e.g. the shopper
  // updated the card, swapped payment method, or removed an OOS item).
  // We DON'T clear on every basket field — just the ones tied to the
  // failure surface area.
  useEffect(() => {
    // KINDW-270 — don't auto-clear an error raised by a failed place-order.
    // `failOrderQuietly` reopens the basket (restores `productItems`), which
    // re-fires this effect; without the guard the "payment failed" banner is
    // wiped the instant it's shown.
    if (placeOrderErrorStickyRef.current) return;
    setPlaceOrderErrorState(null);
  }, [basket?.paymentInstruments, basket?.productItems]);

  const getCheckoutStepName = (step) => CHECKOUT_STEPS_LIST[step];

  const capStepForOutOfStock = useCallback(
    (proposedStep) => {
      if (
        proposedStep === undefined ||
        isInventoryLoading ||
        !hasOutOfStockItems
      ) {
        return proposedStep;
      }

      return Math.min(proposedStep, STEPS.SHIPPING_OPTIONS);
    },
    [hasOutOfStockItems, isInventoryLoading]
  );

  /**
   * Calculate the current step based on basket and customer data.
   * Returns the first incomplete step, or PAYMENT when every prior step is
   * complete. KINDW-1650 — the order is placed directly from the Payment step
   * (no separate review step), so a fully-prepared basket keeps the shopper on
   * PAYMENT to enter their card and place the order.
   *
   * Accepts an optional `basketOverride` so callers that have just mutated the
   * basket (e.g. saving the shipping method) can derive the next step from the
   * fresh server response instead of the stale `basket` captured in this
   * closure — otherwise advancing would take two clicks (the first sees the
   * pre-mutation basket).
   * @param {object} [basketOverride] Fresh basket to evaluate instead of state
   * @returns {number} The calculated step index
   */
  const calculateCurrentStep = (basketOverride) => {
    const activeBasket = basketOverride || basket;

    if (!customer || !activeBasket) {
      return undefined;
    }

    let step = STEPS.PAYMENT;

    if (customer.isGuest && !activeBasket.customerInfo?.email) {
      step = STEPS.CONTACT_INFO;
    } else if (!activeBasket.shipments[0]?.shippingAddress) {
      step = STEPS.SHIPPING_ADDRESS;
    } else if (!activeBasket.shipments[0]?.shippingMethod) {
      step = STEPS.SHIPPING_OPTIONS;
    }

    return step;
  };

  useEffect(() => {
    const newStep = calculateCurrentStep();

    if (newStep === undefined) return;

    setStep((current) => {
      let next;

      // First derivation (mount/refresh): land on whatever step the basket
      // implies — same behaviour as the upstream provider.
      if (current === undefined) {
        next = newStep;
      } else if (newStep <= current) {
        // Backward and same-step transitions (e.g. missing info, explicit
        // edits) always pass through.
        next = newStep;
      } else if (current === STEPS.SHIPPING_OPTIONS && newStep === STEPS.PAYMENT) {
        // FSD DELIVERY-05 #2: progression from SHIPPING_OPTIONS → PAYMENT must
        // be CTA-initiated ("Continue to payment details"). The Delivery step
        // now persists the chosen method on every radio click so the order
        // summary updates in real time (FSD DELIVERY-02 #1); without this
        // guard the basket patch would trigger an auto-advance to PAYMENT the
        // instant a method is selected.
        next = current;
      } else if (current === STEPS.SHIPPING_ADDRESS && newStep > STEPS.SHIPPING_OPTIONS) {
        // FSD DELIVERY-04 #1 / TSD SS-010: after a delivery-address edit,
        // expand the delivery-method step so refreshed methods, prices, and
        // timing are visible instead of skipping straight to PAYMENT with a
        // collapsed summary card.
        next = STEPS.SHIPPING_OPTIONS;
      } else {
        next = newStep;
      }

      return capStepForOutOfStock(next);
    });
  }, [
    capStepForOutOfStock,
    customer?.isGuest,
    basket?.customerInfo?.email,
    basket?.shipments[0]?.shippingAddress,
    basket?.shipments[0]?.shippingMethod,
    basket?.paymentInstruments,
    basket?.billingAddress
  ]);

  useEffect(() => {
    if (isInventoryLoading || step === undefined) {
      return;
    }

    if (!hasOutOfStockItems) {
      return;
    }

    if (step > STEPS.SHIPPING_OPTIONS) {
      setStep(STEPS.SHIPPING_OPTIONS);
    }
  }, [hasOutOfStockItems, isInventoryLoading, step]);

  // KINDW-176 — revert step to SHIPPING_OPTIONS on basket-content change.
  //
  // When the cart changes mid-checkout (the shopper removed a line item, or
  // OOS items were auto-removed), the basket's `shippingMethod` is stale:
  // its price/lead time were computed against the previous line-up.
  // `shipping-options.jsx` already zeroes the stale `c_actualShippingPrice`
  // and clears the form selection; this hook does the matching navigation
  // side-effect so a shopper sitting on PAYMENT (or REVIEW_ORDER) is pulled
  // back to the Delivery Method step to re-confirm.
  //
  // We deliberately do nothing if the shopper hasn't reached SHIPPING_OPTIONS
  // yet — there's no chosen method to invalidate, and pulling them back to
  // the same (or an earlier) step would be jarring.
  const lastCartSignatureRef = useRef(null);

  useEffect(() => {
    const sig = (basket?.productItems ?? [])
      .map((i) => `${i.productId ?? ''}:${i.quantity ?? 1}`)
      .sort()
      .join('|');

    // Defer until we have a real basket — avoids firing the revert on the
    // initial undefined → first-load transition.
    if (!sig) return;

    const previous = lastCartSignatureRef.current;

    if (previous === null) {
      lastCartSignatureRef.current = sig;

      return;
    }

    if (previous === sig) return;

    lastCartSignatureRef.current = sig;

    if (basket?.basketId && basketHasPendingPayPalFlow(basket)) {
      invalidatePayPalFlow(basket.basketId);
    }

    setStep((current) => {
      if (current === undefined) return current;
      if (current <= STEPS.SHIPPING_OPTIONS) return current;

      return capStepForOutOfStock(STEPS.SHIPPING_OPTIONS);
    });
  }, [basket?.basketId, basket?.productItems, capStepForOutOfStock, invalidatePayPalFlow]);

  /** ************** Einstein ****************/
  // Run this once when checkout begins
  useEffect(() => {
    if (basket?.productItems) {
      einstein.sendBeginCheckout(basket);
    }
  }, []);

  // Run this every time checkout steps change
  useEffect(() => {
    if (step != undefined) {
      einstein.sendCheckoutStep(getCheckoutStepName(step), step, basket);
    }
  }, [step]);

  const value = {
    step,
    STEPS,
    calculateCurrentStep,
    goToNextStep: () => setStep(step + 1),
    goToStep: (step) => setStep(step),
    billingSameAsShipping,
    setBillingSameAsShipping,
    marketingOptIn,
    setMarketingOptIn,
    // KINDW-195 — Payment Amount selection (see <PaymentAmount>).
    paymentAmountType,
    setPaymentAmountType,
    isPaymentAmountVisible,
    // KL-1028 — live selected amount (major units) for the Stripe deferred-mode
    // hint; changes in lockstep with `paymentAmountType` (see memo above).
    paymentAmount,
    // KL-1028 — Delivery drives this while methods load / default method applies;
    // payment-amount sync + Stripe deferred amount freeze until it clears.
    shippingTotalsPending,
    setShippingTotalsPending,
    // KINDW-270 / TSD SP-012 — merchant-configurable deposit rate + display
    // copy from the `depositPercentage` site preference (consumed by
    // <PaymentAmount> and the Stripe place-order amount math in index.jsx).
    depositPercentage,
    depositRate,
    isDepositPercentageValid,
    formattedDepositPercentage,
    // KINDW-195 — merchant-configurable balance-due lead time from the
    // `depositBalanceDueDays` site preference; drives the remaining-balance copy.
    balanceDueDays,
    // KINDW-270 — awaited flush of the payment-amount PATCH before place-order.
    flushPaymentAmountBeforePlaceOrder,
    // KINDW-195 — exposed so <PaymentAmount> can resolve per-product
    // `c_isExcludedFromDeposit` for the eligible/ineligible split without
    // firing a parallel `useProducts` (the inventory hook already loaded
    // the same set into React Query's cache).
    productsByProductId,
    // KL-1044 — shopper-scoped warehouse inventory ids so consumers
    // (<PaymentAmount>, place-order amount math) can resolve per-product ATS
    // and treat in-stock items as deposit-ineligible in the eligible/ineligible
    // split — mirrors what this provider passes to the payment-amount helpers.
    shopperInventoryIds,
    // KINDW-195 — PAYMENT-05 #3: inline payment failure message.
    placeOrderError,
    setPlaceOrderError,
    checkoutBannerMessage,
    setCheckoutBannerMessage,
    selectedPaymentProvider,
    setSelectedPaymentProvider,
    isPaymentProviderHydrated,
    PAYMENT_PROVIDER,
    // KL-1105 — BM-driven payment-method availability (single source of truth
    // via SCAPI getPaymentMethodsForBasket). Consumed by the payment partials
    // and the Stripe `<Elements>` provider to show/hide each method.
    isStripeMethodActive,
    isStripeAvailable,
    isPayPalAvailable,
    isPaymentMethodsLoading,
    isPaymentMethodsResolved,
    // KINDW-270 — Stripe PaymentElement completeness (gates "Continue to review").
    isStripePaymentComplete,
    setStripePaymentComplete,
    // KINDW — Stripe payment_method_type selected in the PaymentElement; read by
    // place-order to validate the deposit deny-list at submit time.
    selectedStripePaymentMethodType,
    setSelectedStripePaymentMethodType,
    // KINDW-195 — IM-005: OOS gating exposed to checkout surfaces so the
    // Payment step and Place Order CTAs can disable themselves.
    hasOutOfStockItems,
    isInventoryLoading,
    validateInventoryBeforePlaceOrder,
  };

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
};

CheckoutProvider.propTypes = {
  children: PropTypes.any,
};

/**
 * Checkout step state, payment-amount selection, and inventory gating (from {@link useCheckoutCartInventory}).
 *
 * @returns {{
 *   step: number|undefined,
 *   STEPS: Object,
 *   calculateCurrentStep: () => number|undefined,
 *   goToNextStep: () => void,
 *   goToStep: (step: number) => void,
 *   billingSameAsShipping: boolean,
 *   setBillingSameAsShipping: (value: boolean) => void,
 *   marketingOptIn: boolean,
 *   setMarketingOptIn: (value: boolean) => void,
 *   paymentAmountType: 'full'|'deposit'|null,
 *   setPaymentAmountType: (value: 'full'|'deposit') => void,
 *   isPaymentAmountVisible: boolean,
 *   depositPercentage: number|null,
 *   depositRate: number|null,
 *   isDepositPercentageValid: boolean,
 *   formattedDepositPercentage: string|null,
 *   balanceDueDays: number,
 *   productsByProductId: Object<string, Object>|undefined,
 *   placeOrderError: string|null,
 *   setPlaceOrderError: (message: string|null) => void,
 *   hasOutOfStockItems: boolean,
 *   isInventoryLoading: boolean,
 *   validateInventoryBeforePlaceOrder: () => Promise<{
 *     blocked: boolean,
 *     hasOutOfStockItems: boolean,
 *     outOfStockItemIds: Set<string>,
 *   }>,
 * }}
 */
export const useCheckout = () => {
  return React.useContext(CheckoutContext);
};
